import { t } from '../i18n.js';

  class CleanupForm extends HTMLElement {

    // ── A. Shadow DOM setup ──────────────────────────────────────────────
    constructor() {
      super();
      this._shadow = this.attachShadow({ mode: "open" });
    }

    // ── B. Lifecycle: element added to DOM ───────────────────────────────
    connectedCallback() {
      this._render();
      this._bindEvents();
    }

    // ── C. Option IDs (DRY reference) ────────────────────────────────────
    get _OPT_IDS() {
      return ["opt-cache", "opt-local-storage", "opt-session-storage", "opt-user-stats", "opt-admin-stats"];
    }

    // ── D. Utility: get checkbox state ────────────────────────────────────
    _checked(id) {
      return this._shadow.getElementById(id)?.checked ?? false;
    }

    // ── E. Handle Run cleanup button ──────────────────────────────────────
    _handleRun() {
      const detail = {
        cache:        this._checked("opt-cache"),
        localStorage: this._checked("opt-local-storage"),
        sessionStorage: this._checked("opt-session-storage"),
        userStats:    this._checked("opt-user-stats"),
        adminStats:   this._checked("opt-admin-stats"),
      };

      this.dispatchEvent(new CustomEvent("cleanup-run", {
        detail,
        bubbles:  true,
        composed: true,
      }));

      // Show toast
      const toast    = this._shadow.getElementById("toast");
      const toastMsg = this._shadow.getElementById("toast-msg");
      const labels   = [];
      if (detail.cache)        labels.push(t("cu-cache"));
      if (detail.localStorage) labels.push(t("cu-local-storage"));
      if (detail.sessionStorage) labels.push(t("cu-session-storage"));
      if (detail.userStats)    labels.push(t("cu-user-stats"));
      if (detail.adminStats)   labels.push(t("cu-admin-stats"));
      if (toastMsg) toastMsg.textContent = t("cu-cleared", { items: labels.join(", ") });
      if (toast)    toast.classList.add("visible");
    }

    // ── F. Enable / disable Run button ────────────────────────────────────
    _syncRunBtn() {
      const anyChecked = this._OPT_IDS.some(id => this._checked(id));
      const btn = this._shadow.getElementById("run-btn");
      if (btn) btn.disabled = !anyChecked;
    }

    // ── G. Keep "Select all" in sync ──────────────────────────────────────
    _syncSelectAll() {
      const all  = this._OPT_IDS.every(id =>  this._checked(id));
      const none = this._OPT_IDS.every(id => !this._checked(id));
      const sa   = this._shadow.getElementById("select-all");
      if (!sa) return;
      sa.checked       = all;
      sa.indeterminate = !all && !none;
    }

    // ── H. Sync checked styling on a row ──────────────────────────────────
    _syncRowStyle(optId) {
      const rowMap = {
        "opt-cache":         "row-cache",
        "opt-local-storage": "row-local-storage",
        "opt-session-storage": "row-session-storage",
        "opt-user-stats":    "row-user-stats",
        "opt-admin-stats":   "row-admin-stats",
      };
      const row = this._shadow.getElementById(rowMap[optId]);
      if (row) row.classList.toggle("checked", this._checked(optId));
    }

    // ── I. Toggle all options ─────────────────────────────────────────────
    _toggleAll(checked) {
      this._OPT_IDS.forEach(id => {
        const el = this._shadow.getElementById(id);
        if (el) el.checked = checked;
        this._syncRowStyle(id);
      });
      this._syncRunBtn();
      this._syncSelectAll();
    }

    // ── J. Bind all internal events ───────────────────────────────────────
    _bindEvents() {
      const root = this._shadow;

      root.getElementById("run-btn")?.addEventListener("click", () => this._handleRun());

      this._OPT_IDS.forEach(id => {
        root.getElementById(id)?.addEventListener("change", () => {
          this._syncRowStyle(id);
          this._syncRunBtn();
          this._syncSelectAll();
          root.getElementById("toast")?.classList.remove("visible");
        });
      });

      root.getElementById("select-all")?.addEventListener("change", (e) => {
        this._toggleAll(e.target.checked);
        root.getElementById("toast")?.classList.remove("visible");
      });
    }

    // ── K. Full initial render ────────────────────────────────────────────
    _render() {
      this._shadow.innerHTML = `
        <style>
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

          :host {
            display: block;
            font-family: system-ui, sans-serif;
            color: #1a1a1a;
          }

          .card {
            background: #fff;
            border: 0.5px solid #e0dfd7;
            border-radius: 12px;
            overflow: hidden;
            max-width: 100%;
            margin: 10px;
          }

          /* ── Section title ── */
          .section-title {
            font-size: 11px;
            font-weight: 500;
            color: #aaa;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            padding: 14px 16px 6px;
          }

          /* ── Description ── */
          .desc {
            font-size: 13px;
            color: #888;
            line-height: 1.5;
            padding: 0 16px 12px;
          }

          /* ── Divider ── */
          .divider {
            height: 0.5px;
            background: #e0dfd7;
            margin: 0 16px;
          }

          /* ── Select-all row ── */
          .select-all-row {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 11px 16px;
            cursor: pointer;
            border-bottom: 0.5px solid #e0dfd7;
            background: #f7f6f2;
          }

          .sa-label {
            font-size: 12px;
            font-weight: 500;
            color: #888;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            pointer-events: none;
            flex: 1;
          }

          /* ── Option rows ── */
          .opt-row {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 13px 16px;
            border-bottom: 0.5px solid #e0dfd7;
            cursor: pointer;
            transition: background 0.12s;
            position: relative;
          }

          .opt-row:last-of-type { border-bottom: none; }
          .opt-row:hover { background: #fafaf8; }

          .opt-row.checked {
            background: #fef9f9;
          }

          /* Left accent on checked rows */
          .opt-row.checked::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 2.5px;
            background: #c0392b;
            border-radius: 0 2px 2px 0;
          }

          .opt-icon {
            width: 32px;
            height: 32px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }

          .opt-text  { flex: 1; min-width: 0; }
          .opt-label { font-size: 14px; font-weight: 500; color: #1a1a1a; line-height: 1.3; }
          .opt-sub   { font-size: 11px; color: #aaa; margin-top: 2px; }

          /* ── Custom checkboxes ── */
          input[type="checkbox"] { display: none; }

          .opt-check {
            width: 18px;
            height: 18px;
            border-radius: 5px;
            border: 1.5px solid #d0cfc7;
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: border-color 0.12s, background 0.12s;
          }

          .sa-check {
            width: 16px;
            height: 16px;
            border-radius: 4px;
            border: 1.5px solid #ccc;
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: border-color 0.12s, background 0.12s;
          }

          /* Option checked state (via sibling CSS) */
          #opt-cache:checked ~ .opt-check,
          #opt-local-storage:checked ~ .opt-check,
          #opt-session-storage:checked ~ .opt-check,
          #opt-user-stats:checked ~ .opt-check,
          #opt-admin-stats:checked ~ .opt-check {
            border-color: #5D4037;
            background: #5D4037;
          }

          #opt-cache:checked ~ .opt-check::after,
          #opt-local-storage:checked ~ .opt-check::after,
          #opt-session-storage:checked ~ .opt-check::after,
          #opt-user-stats:checked ~ .opt-check::after,
          #opt-admin-stats:checked ~ .opt-check::after {
            content: "";
            display: block;
            width: 9px;
            height: 5px;
            border-left: 1.5px solid #fff;
            border-bottom: 1.5px solid #fff;
            transform: rotate(-45deg) translateY(-1px);
          }

          /* Select-all checked */
          #select-all:checked + .sa-check {
            border-color: #888;
            background: #888;
          }
          #select-all:checked + .sa-check::after {
            content: "";
            display: block;
            width: 8px;
            height: 4px;
            border-left: 1.5px solid #fff;
            border-bottom: 1.5px solid #fff;
            transform: rotate(-45deg) translateY(-1px);
          }

          /* Select-all indeterminate */
          #select-all:indeterminate + .sa-check {
            border-color: #888;
            background: #888;
          }
          #select-all:indeterminate + .sa-check::after {
            content: "";
            display: block;
            width: 8px;
            height: 1.5px;
            background: #fff;
            border: none;
          }

          /* ── Warning bar ── */
          .warning-bar {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            margin: 12px 16px 0;
            padding: 10px 12px;
            border-radius: 8px;
            background: #fef3c7;
            border: 0.5px solid #fcd34d;
          }

          .warning-icon { flex-shrink: 0; margin-top: 1px; }

          .warning-text {
            font-size: 12px;
            color: #92450a;
            line-height: 1.45;
          }

          /* ── Save bar (footer) ── */
          .save-bar {
            padding: 14px 16px;
            border-top: 0.5px solid #e0dfd7;
            margin-top: 14px;
          }

          .run-btn {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 10px 16px;
            font: inherit;
            font-size: 14px;
            font-weight: 500;
            border-radius: 8px;
            border: none;
            background: #5D4037;
            color: #fff;
            cursor: pointer;
            transition: opacity 0.15s, transform 0.1s;
            touch-action: manipulation;
          }

          .run-btn:hover:not(:disabled)  { opacity: 0.85; }
          .run-btn:active:not(:disabled) { transform: scale(0.98); }
          .run-btn:disabled { opacity: 0.35; cursor: not-allowed; }

          /* ── Toast ── */
          .toast {
            display: none;
            align-items: center;
            gap: 8px;
            background: #e6f4ea;
            color: #0F6E56;
            font-size: 12px;
            padding: 9px 12px;
            border-radius: 8px;
            border: 0.5px solid #b7dfbf;
            margin-top: 10px;
          }
          .toast.visible { display: flex; }
          .toast-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #1D9E75;
            flex-shrink: 0;
          }

          /* ── Responsive ── */
          @media (max-width: 400px) {
            .opt-row { padding: 11px 12px; }
            .section-title, .desc { padding-left: 12px; padding-right: 12px; }
            .warning-bar { margin-left: 12px; margin-right: 12px; }
            .save-bar { padding: 12px; }
          }

          /* ── Dark mode ── */
          /*
          @media (prefers-color-scheme: dark) {
            .card            { background: #1e1e1c; border-color: #333330; }
            .select-all-row  { background: #2a2a28; border-bottom-color: #333330; }
            .sa-label        { color: #555; }
            .opt-row         { border-bottom-color: #333330; }
            .opt-row:hover   { background: #242422; }
            .opt-row.checked { background: #2a1414; }
            .opt-label       { color: #f0ede8; }
            .opt-check       { background: #1e1e1c; border-color: #444441; }
            .sa-check        { background: #1e1e1c; border-color: #444441; }
            .divider         { background: #333330; }
            .warning-bar     { background: #2a1f08; border-color: #4a3010; }
            .warning-text    { color: #d4a03a; }
            .save-bar        { border-top-color: #333330; }
          }
          */
        </style>

        <div class="card">

          <div class="section-title">${t("cu-title")}</div>
          <p class="desc">${t("cu-desc")}</p>

          <!-- Select all -->
          <label class="select-all-row" for="select-all">
            <input type="checkbox" id="select-all" />
            <div class="sa-check"></div>
            <span class="sa-label">${t("cu-select-all")}</span>
          </label>

          <!-- PWA cache -->
          <label class="opt-row" id="row-cache" for="opt-cache">
            <div class="opt-icon" style="background:#5D4037">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2a6 6 0 100 12A6 6 0 008 2z" stroke="#ffffff" stroke-width="1.3"/>
                <path d="M8 5v3l2 2" stroke="#ffffff" stroke-width="1.3"
                      stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="opt-text">
              <div class="opt-label">${t("cu-cache")}</div>
              <div class="opt-sub">${t("cu-cache-sub")}</div>
            </div>
            <input type="checkbox" id="opt-cache" />
            <div class="opt-check"></div>
          </label>

          <!-- localStorage -->
          <label class="opt-row" id="row-local-storage" for="opt-local-storage">
            <div class="opt-icon" style="background:#5D4037">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="4" width="12" height="9" rx="1.5" stroke="#ffffff" stroke-width="1.3"/>
                <path d="M5 7.5h6M5 10h4" stroke="#ffffff" stroke-width="1.2" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="opt-text">
              <div class="opt-label">${t("cu-local-storage")}</div>
              <div class="opt-sub">${t("cu-local-storage-sub")}</div>
            </div>
            <input type="checkbox" id="opt-local-storage" />
            <div class="opt-check"></div>
          </label>

          <!-- sessionStorage -->
          <label class="opt-row" id="row-session-storage" for="opt-session-storage">
            <div class="opt-icon" style="background:#5D4037">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="4" width="12" height="9" rx="1.5" stroke="#ffffff" stroke-width="1.3"/>
                <path d="M5 7.5h6M5 10h4" stroke="#ffffff" stroke-width="1.2" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="opt-text">
              <div class="opt-label">${t("cu-session-storage")}</div>
              <div class="opt-sub">${t("cu-session-storage-sub")}</div>
            </div>
            <input type="checkbox" id="opt-session-storage" />
            <div class="opt-check"></div>
          </label>

          <!-- User stats -->
          <label class="opt-row" id="row-user-stats" for="opt-user-stats">
            <div class="opt-icon" style="background:#5D4037">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="7" cy="6" r="3" stroke="#ffffff" stroke-width="1.3"/>
                <path d="M2 14c0-2.761 2.239-5 5-5s5 2.239 5 5"
                      stroke="#ffffff" stroke-width="1.3" stroke-linecap="round"/>
                <path d="M12 5l1.5 1.5L16 4" stroke="#ffffff" stroke-width="1.2"
                      stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="opt-text">
              <div class="opt-label">${t("cu-user-stats")}</div>
              <div class="opt-sub">${t("cu-user-stats-sub")}</div>
            </div>
            <input type="checkbox" id="opt-user-stats" />
            <div class="opt-check"></div>
          </label>

          <!-- Admin stats -->
          <label class="opt-row" id="row-admin-stats" for="opt-admin-stats">
            <div class="opt-icon" style="background:#5D4037">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="2" width="12" height="12" rx="2" stroke="#ffffff" stroke-width="1.3"/>
                <path d="M5 8h6M5 5.5h6M5 10.5h3" stroke="#ffffff" stroke-width="1.2" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="opt-text">
              <div class="opt-label">${t("cu-admin-stats")}</div>
              <div class="opt-sub">${t("cu-admin-stats-sub")}</div>
            </div>
            <input type="checkbox" id="opt-admin-stats" />
            <div class="opt-check"></div>
          </label>

          <!-- Warning -->
          <div class="warning-bar">
            <span class="warning-icon">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 2L1.5 12h11L7 2z" stroke="#92450a" stroke-width="1.3" stroke-linejoin="round"/>
                <path d="M7 6v3M7 10.5v.5" stroke="#92450a" stroke-width="1.4" stroke-linecap="round"/>
              </svg>
            </span>
            <p class="warning-text">${t("cu-warning")}</p>
          </div>

          <!-- Footer -->
          <div class="save-bar">
            <button class="run-btn" id="run-btn" disabled>
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path d="M3 4h8M5 4V3h4v1M4 4v7a1 1 0 001 1h4a1 1 0 001-1V4"
                      stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              ${t("cu-run")}
            </button>
            <div class="toast" id="toast">
              <span class="toast-dot"></span>
              <span id="toast-msg">${t("cu-completed")}</span>
            </div>
          </div>

        </div>
      `;
    }
  }

export { CleanupForm }